import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Heart, Search } from 'lucide-react';
import { useFavorites, useAuth } from '@/hooks';
import { RecipeGrid } from '@/components/recipes/RecipeGrid';
import { PageHeader } from '@/components/ui/page-header';

export function FavoritesPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Use React Query hooks for favorites (only if authenticated)
  const {
    data: favoritesData,
    isLoading,
    isError,
  } = useFavorites(1, 50, {
    enabled: isAuthenticated,
  });

  // Extract recipes from favorites data
  const favoriteRecipes = favoritesData?.favorites.map((f) => f.recipe) || [];
  const totalFavorites = favoritesData?.total || 0;

  const handleViewRecipe = (recipeId: string) => {
    navigate(`/recipes/${recipeId}`);
  };

  const handleGoToRecipes = () => {
    navigate('/recipes');
  };

  // Not authenticated state
  if (!isAuthenticated) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <Card>
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Acceso requerido</h2>
            <p className="text-gray-600 mb-6">
              Necesitas iniciar sesión para ver tus recetas favoritas.
            </p>
            <Button onClick={() => navigate('/login')}>Iniciar sesión</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <>
        <PageHeader title="Mis Favoritos" icon={<Heart className="h-5 w-5 text-red-500" />} />
        <div className="container mx-auto px-4 py-6 max-w-4xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="h-48 bg-gray-200 rounded-t-lg" />
                <CardContent className="p-4">
                  <div className="h-4 bg-gray-200 rounded mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (isError) {
    return (
      <>
        <PageHeader title="Mis Favoritos" icon={<Heart className="h-5 w-5 text-red-500" />} />
        <div className="container mx-auto px-4 py-6 max-w-4xl">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Error al cargar favoritos</h2>
              <p className="text-gray-600 mb-6">
                No se pudieron cargar tus recetas favoritas. Intenta de nuevo.
              </p>
              <Button onClick={() => window.location.reload()}>Recargar página</Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // Empty state
  if (!isLoading && totalFavorites === 0) {
    return (
      <>
        <PageHeader title="Mis Favoritos" icon={<Heart className="h-5 w-5 text-red-500" />} />
        <div className="container mx-auto px-4 py-6 max-w-4xl">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-gray-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">No hay favoritos</h2>
              <p className="text-gray-600 mb-6">
                Explora recetas y marca las que más te gusten como favoritas para encontrarlas
                fácilmente aquí.
              </p>
              <Button onClick={handleGoToRecipes}>
                <Search className="h-4 w-4 mr-2" />
                Explorar recetas
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Header */}
      <PageHeader title="Mis Favoritos" icon={<Heart className="h-5 w-5 text-red-500" />} />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Stats */}
        <div className="flex justify-center mb-6">
          <Card className="w-full sm:w-auto">
            <CardContent className="p-4 text-center">
              <Heart className="h-6 w-6 text-red-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">{totalFavorites}</div>
              <div className="text-sm text-gray-600">Recetas favoritas</div>
            </CardContent>
          </Card>
        </div>

        {/* Favorites Grid */}
        <RecipeGrid
          recipes={favoriteRecipes}
          onViewRecipe={(recipe) => handleViewRecipe(recipe.id)}
          onAddToMealPlan={(recipe) => {
            console.log('Add to meal plan:', recipe);
          }}
          loading={isLoading}
          variant="default"
          className="mb-8"
        />

        {/* Quick Actions */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-4">¿Quieres agregar más recetas a tus favoritos?</p>
          <Button onClick={handleGoToRecipes}>
            <Search className="h-4 w-4 mr-2" />
            Explorar más recetas
          </Button>
        </div>
      </div>
    </>
  );
}
